
/**
 * AppContext.jsx
 *
 * Provides the global React Context for HomeIQ app state and actions.
 *
 * Responsibilities:
 * - Manages user, log entries, reports, loading, and error state
 * - Exposes login, register, logout, and other actions to the app
 * - Loads user data and reports on mount if logged in
 *
 * Used by: AppProvider (wraps the app), useApp hook, all pages/components
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { AppContext } from './AppContextDef'
import * as api from '../api'

export function AppProvider({ children }) {
  const [user, setUser] = useState(null)
  const [properties, setProperties] = useState([])
  const [reports, setReports] = useState([])
  const [transferRequests, setTransferRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState(null)

  // Tracks the JWT the currently-rendered session was built against. The
  // displayed account (this React tree) and the active credential
  // (localStorage.heirloom_token) are independent, so they can drift apart:
  // another tab logs in/out (localStorage is origin-wide), or the browser
  // restores a frozen page from the back/forward cache after an account
  // switch. When the live token no longer matches what we rendered, the only
  // safe move is a full reload so the app re-initializes against the real
  // session — otherwise a user could act in account A while authenticated as B,
  // or see account A's data after switching to B.
  const sessionTokenRef = useRef(
    typeof localStorage !== 'undefined' ? localStorage.getItem('heirloom_token') : null
  )
  useEffect(() => {
    // After any intentional session change in THIS tab (login/register/logout),
    // the live token is the authoritative one for what we now render.
    sessionTokenRef.current = localStorage.getItem('heirloom_token')
  }, [user])

  useEffect(() => {
    function reconcile() {
      if (localStorage.getItem('heirloom_token') !== sessionTokenRef.current) {
        window.location.reload()
      }
    }
    function onStorage(e) {
      if (e.key === 'heirloom_token') reconcile()
    }
    function onPageShow(e) {
      // Only bfcache restores (persisted) can resurrect a stale rendered
      // session; an ordinary forward navigation re-runs init normally.
      if (e.persisted) reconcile()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  // Each resource is loaded independently so a transient failure in one
  // (e.g. /reports timing out) doesn't bring down the whole session.
  async function loadUserResources(userData) {
    const [reps, props] = await Promise.all([
      api.getReports().catch(() => []),
      api.getProperties().catch(() => []),
    ])
    setUser(userData)
    setReports(reps)
    setProperties(props || [])
    api.getTransferRequests().then(t => setTransferRequests(t || [])).catch(() => {})
    return { user: userData, properties: props || [] }
  }

  // On mount, check for existing token and load user data.
  // Auth failure (invalid/expired token) and resource-load failure are
  // separated: only the former clears tokens and forces re-auth.
  useEffect(() => {
    async function init() {
      if (!api.isLoggedIn()) { setLoading(false); return }
      let userData
      try {
        userData = await api.getMe()
      } catch {
        // Token expired or invalid — clear both tokens
        api.logout()
        setLoading(false)
        return
      }
      // If the loaded user is not an admin, clear any stale impersonation token
      if (!userData.isAdmin) {
        localStorage.removeItem('heirloom_admin_token')
      }
      await loadUserResources(userData)
      setLoading(false)
    }
    init()
  }, [])


  const login = useCallback(async (email, password) => {
    setAuthError(null)
    try {
      const userData = await api.login(email, password)
      return await loadUserResources(userData)
    } catch (err) {
      setAuthError(err.message)
      throw err
    }
  }, [])

  const register = useCallback(async (formData) => {
    setAuthError(null)
    try {
      const userData = await api.register(formData)
      setUser(userData)
      setProperties([])
      setReports([])
      setTransferRequests([])
      return userData
    } catch (err) {
      setAuthError(err.message)
      throw err
    }
  }, [])

  const handleTransfer = useCallback(async (propertyId, requestId, action) => {
    await api.handleTransferRequest(propertyId, requestId, action)
    setTransferRequests(prev => prev.filter(r => r._id !== requestId))
    // Refresh properties and user after transfer
    try {
      const [userData, props] = await Promise.all([api.getMe(), api.getProperties()])
      setUser(userData)
      setProperties(props || [])
    } catch (e) { console.error('Failed to refresh after transfer:', e) }
  }, [])

  const logout = useCallback(() => {
    api.logout()
    setUser(null)
    setProperties([])
    setReports([])
    setTransferRequests([])
  }, [])

  const addReport = useCallback(async (report) => {
    const saved = await api.createReport(report)
    setReports(prev => [saved, ...prev])
    return saved
  }, [])

  const purchaseListingAccess = useCallback(async (listingUserId, address, listingPropertyId) => {
    const saved = await api.purchaseListingAccess(listingUserId, address, listingPropertyId)
    setReports(prev => [saved, ...prev])
    // Refresh user to pick up updated reportsRemaining
    const userData = await api.getMe()
    setUser(userData)
    return saved
  }, [])

  const refreshUser = useCallback(async () => {
    const userData = await api.getMe()
    setUser(userData)
    return userData
  }, [])

  const refreshProperties = useCallback(async () => {
    const props = await api.getProperties()
    setProperties(props || [])
    return props
  }, [])

  const addProperty = useCallback(async ({ address, city, state, zip }) => {
    const property = await api.addProperty({ address, city, state, zip })
    setProperties(prev => [...prev, property])
    return property
  }, [])

  const removeProperty = useCallback(async (propertyId) => {
    await api.removeProperty(propertyId)
    setProperties(prev => prev.filter(p => p._id !== propertyId))
  }, [])

  const loginWithGoogle = useCallback(async (credential) => {
    setAuthError(null)
    try {
      const userData = await api.loginWithGoogle(credential)
      return await loadUserResources(userData)
    } catch (err) {
      setAuthError(err.message)
      throw err
    }
  }, [])

  // Called after an OAuth redirect (Microsoft, Apple) puts a JWT in localStorage
  const loginWithToken = useCallback(async () => {
    setAuthError(null)
    try {
      const userData = await api.getMe()
      const result = await loadUserResources(userData)
      setLoading(false)
      return result
    } catch (err) {
      api.logout()
      setAuthError(err.message)
      setLoading(false)
      throw err
    }
  }, [])

  const clearAuthError = useCallback(() => setAuthError(null), [])

  const contextValue = useMemo(() => ({
    user, setUser, login, loginWithGoogle, loginWithToken, logout, register, refreshUser,
    properties, setProperties, addProperty, removeProperty, refreshProperties,
    reports, addReport, purchaseListingAccess,
    transferRequests, handleTransfer,
    loading, authError, clearAuthError,
  }), [
    user, login, loginWithGoogle, loginWithToken, logout, register, refreshUser,
    properties, addProperty, removeProperty, refreshProperties,
    reports, addReport, purchaseListingAccess,
    transferRequests, handleTransfer,
    loading, authError, clearAuthError,
  ])

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  )
}
